import { TranslocoService } from '@ngneat/transloco';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { UserModel } from 'projects/core/src/lib/models/user.model';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { ConfirmationService } from 'primeng/api';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { faCommentSlash } from '@fortawesome/pro-light-svg-icons';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { ObserverModel } from 'projects/core/src/lib/models/observer.model';

enum EditMode {
  Create = 'create',
  Edit = 'edit',
}

class CustomObserverModel extends UserModel {
  editMode: EditMode;
  silentobserver: boolean;
}

@Component({
  selector: 'app-ac-persons-observers',
  templateUrl: './ac-persons-observers.component.html',
  styleUrls: ['./ac-persons-observers.component.scss']
})
export class AcPersonsObserversComponent implements OnInit, OnChanges {

  @Input() form: UntypedFormGroup;
  @Input() observers: Array<ObserverModel>;
  @Input() ac: AcModel = null;
  @Input() inEdit: boolean;
  @Input() submitting: boolean;

  @Output() sendInvitation = new EventEmitter<any>();

  public dialogVisible = false;
  public dialogMode: EditMode;
  public dialogObserverId: number = null;

  public faCommentSlash = faCommentSlash;

  public participantForm: UntypedFormGroup = this.fb.group({
    observer: new UntypedFormControl(''),
    silentobserver: new UntypedFormControl(''),
  });
  public optionsObservers: Array<CustomObserverModel> = [];
  public acObservers: Array<CustomObserverModel>;

  constructor(
    private fb: UntypedFormBuilder,
    private confirmation: ConfirmationService,
    private transloco: TranslocoService,
    private helper: HelperService,
    private ui: UiService
  ) { }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ac) {
      this.acObservers = this.ac?.observers?.map((observer: ObserverModel) => {
        return new CustomObserverModel().deserialize({
          ...observer,
          mode: EditMode.Edit,
          silentobserver: observer.pivot.type === 'silent'
        });
      });
    }
  }

  public editObserver(observer: CustomObserverModel = null): void {
    if (this.inEdit === true && this.canEditObservers() === true) {
      this.dialogMode = (observer !== null) ? EditMode.Edit : EditMode.Create;
      this.dialogVisible = true;
      this.dialogObserverId = observer?.id || null;
      this.resetForm();
      this.setFormSettings();
      if (observer !== null) {
        this.participantForm.patchValue({
          observer: this.optionsObservers.find(actualUser => actualUser.id === observer.id) || ''
        });
        this.participantForm.get('silentobserver').setValue(observer.silentobserver);
      }
    }
  }

  public cancelObserver(): void {
    if (this.inEdit === true) {
      this.closeAndClearDialog();
    }
  }

  public saveObserver(): void {
    if (this.inEdit === true) {
      if (this.participantForm.valid === true) {
        let changeModeToCreate = false;
        let observer = this.acObservers.find((actualObserver: CustomObserverModel) => {
          return actualObserver.id === this.dialogObserverId;
        }) || null;
        if (
          this.dialogMode === EditMode.Edit &&
          this.participantForm.get('observer').value?.id !== observer?.id
        ) {
          changeModeToCreate = true;
        }
        let data = {};
        const formObserver = this.participantForm.get('observer').value;
        if (formObserver) {
          data = {
            id: formObserver.id,
            gender: formObserver.gender,
            firstname: formObserver.firstname,
            lastname: formObserver.lastname,
            email: formObserver.email,
            phone: formObserver.phone,
            mobile: formObserver.mobile,
            silentobserver: this.participantForm.get('silentobserver').value,
          };
        }
        // create or update observer
        if (this.dialogMode === EditMode.Create) {
          observer = new CustomObserverModel().deserialize({...data, editMode: EditMode.Create});
          this.acObservers.push(observer);
        } else if (observer !== null) {
          if (changeModeToCreate === true) {
            observer.deserialize({...data, editMode: EditMode.Create});
          } else {
            observer.deserialize(data);
          }
        }
        this.closeAndClearDialog();
      } else {
        FormHelper.markFormAsDirty(this.participantForm);
      }
    }
  }

  public deleteObserver(observer: CustomObserverModel): void {
    if (this.inEdit === true && this.acObservers && this.canEditObservers() === true) {
      this.confirmation.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: observer.fullname }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.acObservers = this.acObservers.filter((actualObserver: CustomObserverModel) => {
            return actualObserver.id !== observer.id;
          });
        }
      });
    }
  }

  public getPayload(): Array<any> {
    return this.acObservers.map((observer: CustomObserverModel) => {
      return {
        id: (observer.id) ? observer.id : null,
        silentobserver: observer.silentobserver,
        mode: observer.editMode,
      };
    });
  }

  public canResendEmail(): boolean {
    return this.hasStatus(['planned', 'running']);
  }

  public canEditObservers(): boolean {
    return this.hasStatus(['requested', 'planned']);
  }

  public showSubmitMessages(messages: any): void {
    if (Array.isArray(messages.participants?.not_changed_usersdata)) {
      let observers = this.acObservers.filter((observer: CustomObserverModel) => {
        return messages.participants.not_changed_usersdata.includes(observer.id);
      }).map((observer: CustomObserverModel) => observer.fullname );
      const observerList = observers.filter((value, index, array) => {
        return array.indexOf(value) === index;
      }).join(', ');
      this.ui.showAlert(this.transloco.translate('freeorder.not_changed_usersdata', { users: observerList }));
    }
  }

  private hasStatus(list: Array<string>): boolean {
    return list.find(status => status === this.ac?.status) !== undefined;
  }

  public onResendInvitation(observer: CustomObserverModel, mode: string = null): void {
    this.sendInvitation.emit({
      id: observer.hashed_id,
      name: observer.fullname,
      mode
    });
  }

  private setFormSettings(): void {
    this.optionsObservers = this.observers.map((observer: any) => {
      return new CustomObserverModel().deserialize(observer);
    }).filter((observer: CustomObserverModel) => {
      return this.observerShouldBeInOptionlist(observer);
    }).sort((a: CustomObserverModel, b: CustomObserverModel) => {
      return this.helper.getSortValue(a.lastname, b.lastname);
    });
  }

  private observerShouldBeInOptionlist(observer: CustomObserverModel): boolean {
    return this.acObservers.find((actual: CustomObserverModel) => {
      let result = actual.email === observer.email;
      if (this.dialogMode === EditMode.Edit) {
        result = result && observer.id !== this.dialogObserverId;
      }
      return result;
    }) === undefined;
  }

  private closeAndClearDialog(): void {
    this.dialogVisible = false;
    this.dialogObserverId = null;
    this.clearForm();
    this.resetForm();
  }

  private clearForm(): void {
    this.participantForm.patchValue({
      observer: '',
      silentobserver: '',
    });
  }

  private resetForm(): void {
    this.participantForm.reset();
    this.participantForm.markAsUntouched();
    this.participantForm.markAsPristine();
  }

}
